import {
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import emptyPng from '@assets/empty.png';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Skeleton } from '@components/ui/skeleton';
import { ENoteTypeFile } from '@enums/NoteTypeFile';
import { type Note } from '@models/Note';
import { type IMoveFolderInputData } from '@services/folders/dtos/moveFolder/InputData';
import FolderService from '@services/folders/FolderService';
import { type IMoveNoteInputData } from '@services/notes/dtos/moveNote/InputData';
import NoteService from '@services/notes/NoteService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, LoaderCircle } from 'lucide-react';
import { toast } from 'sonner';

import Item from './Item';

interface IBreadCrumbsFolder {
  id: string | null;
  name: string | null;
}

export interface IOpenMoveFolderInputData {
  category: ENoteTypeFile;
  folderOriginId: string | null;
  id: string;
  title: string;
}

export interface IRefProps {
  close(): void;
  open(inputData: IOpenMoveFolderInputData): void;
}

interface IProps {
  ref: Ref<IRefProps>;
}

function MoveDialog({ ref }: IProps) {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoadingFolders, setIsLoadingFolders] = useState<boolean>(false);
  const folderIdToMove = useRef<string>('0');
  const folderOriginId = useRef<string | null>(null);
  const folderName = useRef<string | null>(null);
  const folderType = useRef<ENoteTypeFile | null>(null);
  const [options, setOptions] = useState<Note[]>([]);
  const [breadcrumbFolders, setBreadcrumbFolders] = useState<
    IBreadCrumbsFolder[]
  >([]);

  const handleTrigger = useCallback(() => {
    setIsOpen((oldState) => !oldState);
  }, []);

  const handleUpdateStack = useCallback(
    (inputData: {
      folderId: string | null;
      folderName: string | null;
    }): void => {
      if (inputData.folderId) {
        setBreadcrumbFolders((currentValue) => [
          ...currentValue,
          {
            id: inputData.folderId,
            name: inputData.folderName!,
          },
        ]);
      } else {
        setBreadcrumbFolders((currentValue) => {
          return currentValue.slice(0, currentValue.length - 1);
        });
      }
    },
    [],
  );

  const loadOptions = useCallback(async (): Promise<void> => {
    setIsLoadingFolders(true);
    try {
      const response = await NoteService.getAll({
        folderId:
          breadcrumbFolders[breadcrumbFolders.length - 1]?.id || undefined,
      });
      setOptions(response.list);
    } finally {
      setIsLoadingFolders(false);
    }
  }, [breadcrumbFolders]);

  useEffect(() => {
    loadOptions();
  }, [breadcrumbFolders, loadOptions]);

  const closeModal = useCallback((): void => {
    handleTrigger();
    setBreadcrumbFolders([]);
  }, [handleTrigger]);

  const handleNavigate = useCallback(
    (inputData: IOpenMoveFolderInputData): void => {
      handleUpdateStack({
        folderId: inputData.id,
        folderName: inputData.title,
      });
    },
    [handleUpdateStack],
  );

  useImperativeHandle(
    ref,
    () => ({
      open: (inputData: IOpenMoveFolderInputData) => {
        folderIdToMove.current = inputData.id;
        folderOriginId.current = inputData.folderOriginId;
        folderName.current = inputData.title;
        folderType.current = inputData.category;
        handleTrigger();
      },
      close: () => {
        closeModal();
      },
    }),
    [closeModal, handleTrigger],
  );

  const { isPending: isPendingMoveNote, mutateAsync: mutateAsyncMoveNote } =
    useMutation<void, unknown, IMoveNoteInputData>({
      mutationFn: NoteService.move,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
    });

  const { isPending: isPendingMoveFolder, mutateAsync: mutateAsyncMoveFolder } =
    useMutation<void, unknown, IMoveFolderInputData>({
      mutationFn: FolderService.move,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
    });

  const handleMoveFolder = useCallback(
    async (data: IMoveFolderInputData) => {
      await mutateAsyncMoveFolder(data);
    },
    [mutateAsyncMoveFolder],
  );

  const handleMoveNote = useCallback(
    async (data: IMoveNoteInputData) => {
      await mutateAsyncMoveNote(data);
    },
    [mutateAsyncMoveNote],
  );

  const handleSubmit = useCallback(async (): Promise<void> => {
    try {
      if (breadcrumbFolders.at(-1)!.id === folderOriginId.current) {
        closeModal();
        return;
      }
      if (folderType.current === ENoteTypeFile.Folder) {
        await handleMoveFolder({
          id: folderIdToMove.current,
          folderIdDestination: breadcrumbFolders.at(-1)!.id || null,
        });
      } else {
        await handleMoveNote({
          id: folderIdToMove.current,
          folderIdDestination: breadcrumbFolders.at(-1)!.id || null,
        });
      }
      toast.success('Sucesso', {
        description: 'Pasta movida com sucesso',
      });
    } catch {
      //
    } finally {
      closeModal();
    }
  }, [breadcrumbFolders, closeModal, handleMoveFolder, handleMoveNote]);

  return (
    <Dialog open={isOpen}>
      <DialogContent
        aria-describedby={undefined}
        className="flex max-h-[80%] flex-col sm:max-w-[425px] [&>button]:hidden">
        <DialogHeader>
          <DialogTitle className="text-primary">
            {breadcrumbFolders.at(-1)?.name || 'Início'}
          </DialogTitle>
        </DialogHeader>
        <div className="text-tertiary flex flex-row items-center">
          {breadcrumbFolders.at(-1) && (
            <button
              className="text-tertiary flex cursor-pointer flex-row items-center"
              onClick={() =>
                handleUpdateStack({
                  folderId: null,
                  folderName: null,
                })
              }
              title="Voltar">
              <ChevronLeft className="size-6 sm:size-8" />
              Voltar
            </button>
          )}
        </div>

        {isLoadingFolders ? (
          <div className="flex flex-col gap-4">
            <Skeleton className={`h-10 w-[370px]`} />
            <Skeleton className={`h-10 w-[370px]`} />
            <Skeleton className={`h-10 w-[370px]`} />
            <Skeleton className={`h-10 w-[370px]`} />
            <Skeleton className={`h-10 w-[370px]`} />
          </div>
        ) : (
          <div className="flex max-h-[80%] flex-col overflow-y-auto">
            {options.length ? (
              <div className="w-full">
                {options
                  .filter((item) => item.id !== folderIdToMove.current)
                  .map((item) => (
                    <Item
                      data={item}
                      key={item.id}
                      onNavigate={handleNavigate}
                    />
                  ))}
              </div>
            ) : (
              <div className="flex flex-col justify-center">
                <img
                  alt="Imagem de listagem vazia"
                  className="mx-auto max-w-72"
                  src={emptyPng}
                />
                <p className="mt-4 text-center text-lg font-bold">
                  Não há registros!
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-y-4">
          <Button
            className="cursor-pointer"
            disabled={isPendingMoveNote || isPendingMoveFolder}
            onClick={closeModal}
            type="button"
            variant="destructive">
            Cancelar
          </Button>
          <Button
            className="min-w-28 cursor-pointer"
            disabled={isPendingMoveNote || isPendingMoveFolder}
            onClick={handleSubmit}
            type="button"
            variant="secondary">
            {isPendingMoveNote || isPendingMoveFolder ? (
              <LoaderCircle className="text-tertiary size-8 animate-spin" />
            ) : (
              'Mover'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MoveDialog;
